/**
 * Campanha Black November no painel.
 *
 * A janela, a meta e a fase vivem no **backend** (`GET /api/campaigns/<slug>/`),
 * que resolve tudo no fuso de Brasília para o ano corrente. Aqui ficam só o
 * espelho dos nomes de fase, a formatação em reais e a leitura defensiva do
 * payload — nada de datas, para não existir uma segunda régua para a mesma
 * campanha.
 */

import { formatMoney } from '@/utils/currency';

/** A campanha vive dentro do Cakto Rewards. */
export const CAMPAIGN_AWARDS_PATH = '/dashboard/awards';

/** Espelho de `campaign.campaigns.CampaignPhase` no backend. */
export const CAMPAIGN_PHASE = {
  HIDDEN: 'hidden',
  TEASER: 'teaser',
  RUNNING: 'running',
  RESULTS_WON: 'results_won',
  RESULTS_MISSED: 'results_missed',
};

export const isResultsPhase = (phase) =>
  phase === CAMPAIGN_PHASE.RESULTS_WON || phase === CAMPAIGN_PHASE.RESULTS_MISSED;

const toAmount = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) && amount > 0 ? amount : 0;
};

const toWholeNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? Math.floor(number) : 0;
};

/**
 * Normaliza o payload da API para o que a faixa e o dialog consomem.
 *
 * Devolve `null` quando não há campanha para mostrar — sem resposta, resposta
 * malformada ou fase `hidden`. O componente que recebe `null` não renderiza, e é
 * assim que a campanha sai do ar sozinha depois de 15 de dezembro.
 */
export const parseCampaignProgress = (payload) => {
  if (!payload || typeof payload !== 'object') return null;

  const { phase } = payload;

  if (!phase || phase === CAMPAIGN_PHASE.HIDDEN) return null;

  const goal = toAmount(payload.goal);
  const sales = toAmount(payload.sales);

  return {
    phase,
    goal,
    sales,
    missing: toAmount(payload.missing),
    percent: Math.min(100, toWholeNumber(payload.percent)),
    daysLeft: toWholeNumber(payload.days_left),
    achieved: payload.achieved === true,
    year: toWholeNumber(payload.year),
    name: typeof payload.name === 'string' ? payload.name : 'Black November',
  };
};

/** A campanha é em reais por definição — não existe Black November em outra moeda. */
export const formatCampaignMoney = (value, { cents = false } = {}) =>
  formatMoney(value, 'BRL', {
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: cents ? 2 : 0,
  });

/**
 * `R$ 100 mil` para caber no espaço curto da faixa e dos marcos da régua. É a
 * forma da comunicação oficial da campanha (www.cakto.com.br/black-friday), que
 * guarda o "100K" para o nome da peça.
 */
export const formatCampaignGoalShort = (value) => {
  const amount = Number(value || 0);

  if (amount >= 1000000) return `R$ ${String(amount / 1000000).replace('.', ',')} mi`;
  if (amount >= 1000) return `R$ ${String(amount / 1000).replace('.', ',')} mil`;

  return formatCampaignMoney(amount);
};

/**
 * Preenche `{year}`, `{goal}`, `{goalShort}` e `{sales}` do copy com o que a API respondeu.
 *
 * Existe para que nenhum ano nem valor fique escrito no texto: a campanha volta
 * em 2027 lendo o mesmo arquivo, e a meta exibida é sempre a que o backend
 * mediu.
 */
export const fillCampaignText = (text, { year, goal, sales } = {}) => {
  if (typeof text !== 'string') return '';

  return text
    .replace('{year}', year != null ? String(year) : '')
    .replace('{goalShort}', goal != null ? formatCampaignGoalShort(goal) : '')
    .replace('{goal}', goal != null ? formatCampaignMoney(goal) : '')
    .replace('{sales}', sales != null ? formatCampaignMoney(sales, { cents: true }) : '');
};
